/**
 * Duel v3 P0 FX (lane FX-PIXEL P4, user request 004 + the player's #1 complaint: "the effects cover both fighters").
 * Between the two fighters of a bout (a duel or a player bout, while they fight):
 * - a plain blow (a hit, a parry, an interrupt, a dodge) shows only a small pixel spark: a 5-px diamond with a hard
 *   onset, gone in ~150 ms (9yin timing, docs/research/C0_FX_WUXIA_SWORD.md); the big effects stay for a guard
 *   shatter, a counter, an ultimate (power move) and the KO;
 * - a swing (a basic blow, an attack or break move) leaves a thin trail along the blade tip's real path (the rig's
 *   `BladeLine` of the frame the renderer drew, `AnimState.lastSwing`), with a 1-2 frame glint at the head, instead
 *   of the stamped arcs;
 * - dash afterimages ('ghost') are kept as they are (the user likes them solid).
 * Wired without touching effects.ts: an effect factory registered with no fx ids (render/fx/registry.ts) wraps the
 * Effects' `handle` once (render3d/core/view.ts does the same), lets it run, then trims what that event added.
 * Everything is drawn as whole art pixels in master-palette ramp colours, with a screen-locked checker for fading, so
 * it is pixel-clean with or without the FX pen (render/fx/pixelRules.ts).
 */
import type { Entity, World, WorldEvent } from '@nine/shared';
import { SKILLS } from '@nine/shared';
import { bladesIn } from '../rig/sets';
import { viewOf, visualScale } from '../sprites';
import { FX_RAMPS } from './pixelRules';
import { registerFx, type FxHost } from './registry';

type V = [number, number];
const RAMP = FX_RAMPS as Record<string, readonly string[]>;

// ── Who is in a bout ──

/** The two fighters of a bout under way (fighting), or null. */
function boutPair(w: World): [number, number] | null {
  const pb = w.pvpBout;
  if (pb?.fight) return [pb.a, pb.b];
  const d = w.duel as { phase?: string; foeId?: number } | null;
  if (d && d.phase === 'fight' && typeof d.foeId === 'number' && d.foeId >= 0) return [w.player.id, d.foeId];
  return null;
}
const inPair = (p: [number, number] | null, id: number | undefined): boolean => !!p && id !== undefined && (id === p[0] || id === p[1]);

// ── Blade-tip history (per fighter, while it swings) ──

interface Tip {
  t: number;
  tip: V;
  hand: V;
}
const HISTORY_S = 0.16;

class BladeTrails {
  private readonly tips = new Map<number, Tip[]>();
  constructor(private readonly host: FxHost) {}

  /** Samples the blade of each bout fighter from the frame the renderer drew last (`lastSwing`). */
  step(pair: [number, number] | null): void {
    const w = this.host.world;
    for (const [id, list] of this.tips) {
      while (list.length && w.time - list[0]!.t > HISTORY_S) list.shift();
      if (!list.length) this.tips.delete(id);
    }
    if (!pair) return;
    for (const id of pair) {
      const e = w.get(id);
      if (!e || e.dead) continue;
      const img = this.host.anim(id).lastSwing;
      if (!img) continue;
      const b = bladesIn(img)[0];
      const sp = this.host.sprites.of(e);
      if (!b || !sp) continue;
      const { set, facing } = viewOf(sp, this.host.iso.heading(e), this.host.iso.facing(e));
      const s = visualScale(e, set);
      const [x, y0] = this.host.pos(e);
      const y = y0 - e.z;
      const at = (bx: number, by: number): V => [x + facing * (bx - set.ax) * s, y + (by - set.ay) * s];
      const tip = at(b.hx + b.dx * b.len, b.hy + b.dy * b.len);
      const hand = at(b.hx, b.hy);
      // A tip far from the body is a frame the blade data does not fit (a held or swapped frame): skipped.
      const set2 = this.host.sprites.of(e);
      const reach = ((set2?.h ?? 40) * s) * 0.9;
      if (Math.hypot(tip[0] - x, tip[1] - (y - reach * 0.5)) > reach) continue;
      let list = this.tips.get(id);
      if (!list) this.tips.set(id, (list = []));
      const last = list[list.length - 1];
      // A jump (a new swing, a turn) starts a new trail instead of a line across the screen.
      if (last && Math.hypot(last.tip[0] - tip[0], last.tip[1] - tip[1]) > reach * 0.6) list.length = 0;
      if (list.length > 4) list.splice(0, list.length - 4);
      // One sample per drawn frame change (the rig steps at ~10 fps): repeats only refresh the time.
      if (last && Math.hypot(last.tip[0] - tip[0], last.tip[1] - tip[1]) < 0.5) last.t = w.time;
      else list.push({ t: w.time, tip, hand });
    }
  }

  of(id: number): readonly Tip[] {
    return this.tips.get(id) ?? [];
  }
}

// ── Pixel drawing (whole art pixels, ramp colours, screen-locked checker) ──

function cell(g: CanvasRenderingContext2D, x: number, y: number, css: string, half = false): void {
  const i = Math.round(x);
  const j = Math.round(y);
  if (half && ((i + j) & 1) !== 0) return;
  g.fillStyle = css;
  g.fillRect(i, j, 1, 1);
}
function line(g: CanvasRenderingContext2D, a: V, b: V, css: string, half = false): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const n = Math.max(1, Math.round(Math.max(Math.abs(dx), Math.abs(dy))));
  for (let i = 0; i <= n; i++) cell(g, a[0] + (dx * i) / n, a[1] + (dy * i) / n, css, half);
}
/** Sample B's spark: a diamond of radius `r` (2 = 5 px across) with a light core. */
function diamond(g: CanvasRenderingContext2D, x: number, y: number, r: number, body: string, core: string): void {
  for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) if (Math.abs(i) + Math.abs(j) <= r) cell(g, x + i, y + j, body);
  cell(g, x, y, core);
}

/** The trail along recent blade tips: a 1-2 px ribbon (core + edge toward the hand), the oldest part a checker. */
function drawTrail(g: CanvasRenderingContext2D, tips: readonly Tip[], ox: number, oy: number, now: number, edge: string): void {
  if (tips.length < 2) return;
  const W = RAMP.white!;
  const pts: { p: V; h: V; u: number }[] = [];
  const P = tips.map((t) => t.tip);
  const H = tips.map((t) => t.hand);
  const n = tips.length;
  for (let i = 0; i + 1 < n; i++) {
    const steps = Math.max(2, Math.round(Math.hypot(P[i + 1]![0] - P[i]![0], P[i + 1]![1] - P[i]![1])));
    for (let k = 0; k < steps; k++) {
      const u = k / steps;
      // Straight between the drawn frames' tips (a spline overshoots on the rig's coarse steps).
      const q = (A: V[], c: 0 | 1) => A[i]![c] + (A[i + 1]![c] - A[i]![c]) * u;
      pts.push({ p: [q(P, 0) + ox, q(P, 1) + oy], h: [q(H, 0) + ox, q(H, 1) + oy], u: (i + u) / (n - 1) });
    }
  }
  const head = tips[n - 1]!;
  pts.push({ p: [head.tip[0] + ox, head.tip[1] + oy], h: [head.hand[0] + ox, head.hand[1] + oy], u: 1 });
  // The whole trail ages out: younger than 2 frames solid, then the older half as a checker.
  const age = now - head.t;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const half = a.u < 0.5 || age > 0.09;
    if (a.u > 0.45) {
      // The edge row, toward the hand (the lit core stays on the outside).
      const d: V = [a.h[0] - a.p[0], a.h[1] - a.p[1]];
      const l = Math.hypot(d[0], d[1]) || 1;
      line(g, [a.p[0] + d[0] / l, a.p[1] + d[1] / l], [b.p[0] + d[0] / l, b.p[1] + d[1] / l], edge, half);
    }
    line(g, a.p, b.p, a.u > 0.3 ? W[8]! : edge, half);
  }
  // The glint: a 1-2 frame plus at the head while the blade still moves fast.
  if (age < 0.1 && n >= 2) {
    const prev = tips[n - 2]!;
    if (Math.hypot(head.tip[0] - prev.tip[0], head.tip[1] - prev.tip[1]) > 4) {
      const x = head.tip[0] + ox;
      const y = head.tip[1] + oy;
      cell(g, x - 1, y, W[8]!);
      cell(g, x + 1, y, W[8]!);
      cell(g, x, y - 1, W[8]!);
      cell(g, x, y + 1, W[8]!);
      cell(g, x, y, W[6]!);
    }
  }
}

// ── The hook ──

type Host = FxHost & {
  handle(ev: WorldEvent): void;
  flash: { col: string; a: number };
  punch: number;
};

/** Is this a plain blow (small spark only)? Big: guard shatter/break, counter, an ultimate (power move), the KO. */
function plainHit(ev: Extract<WorldEvent, { type: 'hit' }>, tgt: Entity | undefined): boolean {
  if (ev.outcome === 'guardShatter' || ev.outcome === 'guardBreak' || ev.outcome === 'countered') return false;
  if (SKILLS[ev.skillId]?.kind === 'power') return false;
  if (!tgt || tgt.dead || tgt.hp <= 0) return false;
  return true;
}
/** A swing whose stamped arcs give way to the blade trail: a basic blow, an attack or a break move. */
function swingEvent(ev: WorldEvent): { srcId: number; tgtId?: number | null } | null {
  if (ev.type === 'basic') return { srcId: ev.srcId, tgtId: ev.tgtId };
  if (ev.type === 'skill') {
    const k = SKILLS[ev.skillId]?.kind;
    if (k === 'attack' || k === 'break') return { srcId: ev.srcId, tgtId: ev.tgtId };
  }
  return null;
}

registerFx([], (base) => {
  const host = base as Host;
  const trails = new BladeTrails(host);
  const handle = host.handle.bind(host);
  host.handle = (ev: WorldEvent): void => {
    const w = host.world;
    const pair = boutPair(w);
    const hit = ev.type === 'hit' && inPair(pair, ev.srcId) && inPair(pair, ev.tgtId) ? ev : null;
    const swing = swingEvent(ev);
    const swung = swing && inPair(pair, swing.srcId) ? swing : null;
    const tgt = hit ? w.get(hit.tgtId) : undefined;
    const trim = (hit && plainHit(hit, tgt)) || !!swung;
    if (!trim) return handle(ev);
    const fx0 = host.fx.length;
    const parts0 = host.parts.length;
    const shake0 = host.shake;
    const flash0 = host.flash.a;
    const punch0 = host.punch;
    handle(ev);
    // Keep the afterimages; drop the rest this event added (arcs, sheets, sparks, rings, ink marks).
    const added = host.fx.splice(fx0);
    for (const f of added) if (f.type === 'ghost') host.fx.push(f);
    host.parts.length = Math.min(host.parts.length, parts0);
    host.shake = Math.max(shake0, Math.min(host.shake, 1.2));
    host.flash.a = flash0;
    host.punch = punch0;
    if (hit && tgt) spark(host, hit, tgt);
    if (swung) trail(host, trails, swung.srcId);
  };
  return {
    step: () => trails.step(boutPair(host.world)),
  };
});

/** The plain blow's mark: one 5-px diamond at the contact point, solid ~2 frames then a plus, gone by 150 ms. */
function spark(host: Host, ev: Extract<WorldEvent, { type: 'hit' }>, tgt: Entity): void {
  if (ev.outcome === 'dodge') return;
  const src = host.world.get(ev.srcId);
  const [x, y] = host.pos(tgt);
  const set = host.sprites.of(tgt);
  const h = (set?.h ?? 40) * visualScale(tgt, set);
  const toward = src ? Math.sign(host.pos(src)[0] - x) || 1 : 1;
  const cx = Math.round(x + toward * 5);
  const cy = Math.round(y - tgt.z - h * 0.55);
  const ramp = ev.outcome === 'blocked' ? RAMP.steel! : ev.outcome === 'interrupt' ? RAMP.azure! : RAMP.gold!;
  const big = ev.crit ? 3 : 2;
  host.fx.push({
    type: 'custom',
    x: cx,
    y: cy,
    t: 0,
    life: 0.15,
    draw: (g, px, py, k) => {
      if (k < 0.55) diamond(g, px, py, big, ramp[4]!, ramp[6] ?? ramp[5]!);
      else diamond(g, px, py, 1, ramp[5]!, ramp[6] ?? ramp[5]!);
    },
  });
}

/** The swing's trail, drawn from the blade-tip history for as long as the swing lasts. */
function trail(host: Host, trails: BladeTrails, srcId: number): void {
  const a = host.anim(srcId);
  const edge = RAMP.azure![4]!;
  host.fx.push({
    type: 'custom',
    x: 0,
    y: 0,
    t: 0,
    life: Math.max(0.2, a.swingDur + 0.12),
    draw: (g, _x, _y, _k, ox, oy) => drawTrail(g, trails.of(srcId), ox, oy, host.world.time, edge),
  });
}
